import { PositionAnalysis } from '@/types/portfolio';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown, Trash2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PositionCardProps {
  analysis: PositionAnalysis;
  onDelete: (id: string) => void;
}

export function PositionCard({ analysis, onDelete }: PositionCardProps) {
  const isGain = analysis.gainPct >= 0;
  const hasRecommendation = analysis.recommendedSellQty > 0;
  const gainPctValue = analysis.gainPct * 100;
  
  // Get currency symbol based on local currency
  const getCurrencySymbol = (currency?: string): string => {
    if (!currency) return '$';
    switch (currency.toUpperCase()) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'CHF': return 'CHF';
      case 'GBP': return '£';
      case 'CAD': return 'CA$';
      case 'JPY': return '¥';
      default: return currency;
    }
  };
  
  const currencySymbol = getCurrencySymbol(analysis.localCurrency);
  
  // Determine threshold level and color
  const getThresholdInfo = () => {
    if (gainPctValue >= 45) return { 
      level: 3, 
      color: 'bg-red-500', 
      border: 'border-red-500',
      emoji: '🎯'
    };
    if (gainPctValue >= 30) return { 
      level: 2, 
      color: 'bg-orange-500', 
      border: 'border-orange-500',
      emoji: '⚡'
    };
    if (gainPctValue >= 15) return { 
      level: 1, 
      color: 'bg-yellow-500', 
      border: 'border-yellow-500',
      emoji: '💰'
    };
    return null;
  };
  
  const thresholdInfo = getThresholdInfo();
  
  // Calculate dynamic recovery percentage for the badge
  const recoveryPercentage = analysis.initialInvestment 
    ? ((analysis.recommendedSellQty * analysis.currentPrice / analysis.initialInvestment) * 100).toFixed(0)
    : 0;
  
  return (
    <Card className={cn(
      "p-6 transition-all duration-300 hover:shadow-lg relative overflow-hidden h-full flex flex-col",
      thresholdInfo && thresholdInfo.border
    )}>
      {/* Capital Recovery Alert Banner */}
      {thresholdInfo && (
        <div className={cn(
          "mb-4 p-4 rounded-lg border-2",
          thresholdInfo.border,
          "bg-gradient-to-r from-transparent to-white/5"
        )}>
          <div className="flex items-center justify-between mb-3">
            <Badge className={cn(
              "text-sm font-bold text-white border-0 px-4 py-1.5",
              thresholdInfo.color
            )}>
              {thresholdInfo.emoji} {gainPctValue.toFixed(0)}% - RECOVER {recoveryPercentage}%
            </Badge>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Capital Recovered</div>
              <div className="text-xl font-bold text-foreground">
                {recoveryPercentage}%
              </div>
            </div>
          </div>
          
          {/* Action Details */}
          <div className="grid grid-cols-2 gap-4 mt-3 p-3 bg-background/50 rounded-lg">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Sell Shares</div>
              <div className="text-lg font-bold text-foreground">
                {analysis.recommendedSellQty} shares
              </div>
              <div className="text-xs text-muted-foreground">
                ({analysis.recommendedSellPct}% of position)
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Recover Amount</div>
              <div className="text-lg font-bold text-green-600 dark:text-green-400">
                {currencySymbol}{(analysis.recommendedSellQty * analysis.currentPrice).toLocaleString()}
              </div>
            </div>
          </div>
          
          {/* Progress Bar for Capital Recovery */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-muted-foreground">Capital Recovery Progress</span>
              <span className="text-xs font-bold text-foreground">
                {analysis.capitalRecoveredPct}% / 100%
              </span>
            </div>
            <Progress 
              value={Math.min(analysis.capitalRecoveredPct || 0, 100)} 
              className={cn(
                "h-2",
                (analysis.capitalRecoveredPct || 0) >= 100 && "[&>div]:bg-green-500",
                (analysis.capitalRecoveredPct || 0) >= 40 && (analysis.capitalRecoveredPct || 0) < 100 && "[&>div]:bg-orange-500",
                (analysis.capitalRecoveredPct || 0) >= 20 && (analysis.capitalRecoveredPct || 0) < 40 && "[&>div]:bg-yellow-500"
              )}
            />
          </div>
        </div>
      )}
      
      <div className="flex items-start justify-between mb-4">
        <div className="space-y-1">
          <h3 className="text-2xl font-bold text-foreground">{analysis.ticker}</h3>
          {analysis.stockCode && analysis.stockCode !== analysis.ticker && (
            <p className="text-sm font-semibold text-primary">Stock Code: {analysis.stockCode}</p>
          )}
          {analysis.isin && (
            <p className="text-xs text-muted-foreground font-mono">ISIN: {analysis.isin}</p>
          )}
          <p className="text-sm text-muted-foreground">
            {analysis.quantity} shares @ {currencySymbol}{analysis.buyPrice.toFixed(2)}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(analysis.id)}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="space-y-3 flex-1">
        {analysis.initialInvestment !== undefined && analysis.initialInvestment !== 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Initial Investment</span>
            <span className="text-lg font-semibold text-primary">
              {currencySymbol}{analysis.initialInvestment.toLocaleString()}
            </span>
          </div>
        )}
        
        {thresholdInfo && analysis.recommendedSellQty > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total Investment after Sale</span>
            <span className="text-lg font-semibold text-primary">
              {currencySymbol}{((analysis.quantity - analysis.recommendedSellQty) * analysis.buyPrice).toLocaleString()}
            </span>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Price per Share (Buy)</span>
          <span className="text-lg font-semibold">
            {currencySymbol}{analysis.buyPrice.toFixed(2)}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Current Live Price</span>
          <span className={cn(
            "text-lg font-semibold",
            analysis.currentPrice > analysis.buyPrice ? "text-success" : "text-danger"
          )}>
            {currencySymbol}{analysis.currentPrice.toFixed(2)}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Total Value</span>
          <div className="text-right">
            <div className="text-lg font-semibold">
              {analysis.totalValueCHF?.toLocaleString()} CHF
            </div>
            <div className="text-xs text-muted-foreground">
              {analysis.totalValue.toLocaleString()} {analysis.localCurrency}
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Gain/Loss</span>
          <div className="flex items-center gap-2">
            {isGain ? (
              <TrendingUp className="h-4 w-4 text-success" />
            ) : (
              <TrendingDown className="h-4 w-4 text-danger" />
            )}
            <span className={cn(
              "text-lg font-bold",
              isGain ? "text-success" : "text-danger"
            )}>
              {isGain ? '+' : ''}{(analysis.gainPct * 100).toFixed(2)}%
            </span>
          </div>
        </div>

        {analysis.addedValueChf !== undefined && analysis.addedValueChf !== 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Added Value (CHF)</span>
            <span className={cn(
              "text-lg font-semibold",
              analysis.addedValueChf >= 0 ? "text-success" : "text-red-500"
            )}>
              {Math.round(analysis.addedValueChf).toLocaleString()} CHF
            </span>
          </div>
        )}
      </div>
      
      {hasRecommendation && (
        <div className="mt-auto pt-4">
          <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg border-2 border-primary/30">
          <div className="flex items-start gap-3">
            <div className={cn(
              "p-2 rounded-full",
              thresholdInfo?.color
            )}>
              <AlertCircle className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-foreground text-lg">Action Required</p>
              <p className="text-sm text-muted-foreground mt-1">
                Sell {analysis.recommendedSellQty} shares and that represents <span className="text-orange-500 font-bold animate-pulse">{analysis.initialInvestment ? ((analysis.recommendedSellQty * analysis.currentPrice / analysis.initialInvestment) * 100).toFixed(2) : 0}%</span> of your initial investment recover
              </p>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="p-3 bg-background rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Expected Proceeds</div>
                  <div className="text-base font-bold text-green-600 dark:text-green-400">
                    {currencySymbol}{(analysis.recommendedSellQty * analysis.currentPrice).toLocaleString()}
                  </div>
                </div>
                <div className="p-3 bg-background rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Remaining at Risk</div>
                  <div className="text-base font-bold text-foreground">
                    {currencySymbol}{((analysis.initialInvestment || 0) - (analysis.recommendedSellQty * analysis.currentPrice)).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}
    </Card>
  );
}
